import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../utils/ThemeSwitcher';
import {
  CheckCircle2, Layers, Target, Clock, LineChart, MapPin,
  FileSearch, Brain, Clipboard, ArrowRight, Globe, Star,
  Database, Zap, Infinity as InfinityIcon, Check, Moon, Sun,
  Monitor, Smartphone, Download,
} from 'lucide-react';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';
import './DataQuadLanding.css';
 
// ============================================================================
// HOOKS
// ============================================================================
const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold, rootMargin: '-40px' }
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, [threshold]);
  return [ref, isVisible];
};
 
const useCountUp = (end, duration = 1600, startWhen = true) => {
  const [value, setValue] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!startWhen || started.current) return;
    started.current = true;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(eased * end);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [startWhen, end, duration]);
  return value;
};
 
// Parallax Y offset — used by hero glow
const useParallaxY = (factor = 0.25) => {
  const [y, setY] = useState(0);
  useEffect(() => {
    const onScroll = () => setY(window.scrollY * factor);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [factor]);
  return y;
};
 
// ============================================================================
// DATA
// ============================================================================
const features = [
  { id: 'completeness', label: 'Completeness', icon: CheckCircle2, color: '#10B981',
    title: 'Completeness Analysis',
    desc: 'Track missing data patterns across variables, collectors, and time periods. Instantly see which questions have the highest non-response rates.',
    screenshotDark: '/completeness_dark.png', screenshotLight: '/completeness_light.png' },
  { id: 'duplicate', label: 'Duplicates', icon: Layers, color: '#F59E0B',
    title: 'Duplicate Detection',
    desc: 'Identify exact and fuzzy duplicate records using configurable multi-variable pattern matching rules.',
    screenshotDark: '/uniqueness_dark.png', screenshotLight: '/uniqueness_light.png' },
  { id: 'outlier', label: 'Outliers', icon: Target, color: '#EF4444',
    title: 'Outlier Detection',
    desc: 'Statistical outlier identification using IQR and Z-score methods with adjustable sensitivity thresholds.',
    screenshotDark: '/outliers_dark.png', screenshotLight: '/outliers_light.png' },
  { id: 'duration', label: 'Duration', icon: Clock, color: '#8B5CF6',
    title: 'Duration Analysis',
    desc: 'Monitor interview duration patterns and flag suspiciously short or long responses that may indicate fabrication.',
    screenshotDark: '/duration_dark.png', screenshotLight: '/duration_light.png' },
  { id: 'surveyflow', label: 'Survey Flow', icon: LineChart, color: '#F97316',
    title: 'Survey Flow Analysis',
    desc: 'Visualize response paths with Sankey diagrams, identify drop-off patterns, and track collector consistency.',
    screenshotDark: '/surveyflow_dark.png', screenshotLight: '/surveyflow_light.png' },
  { id: 'geolocation', label: 'Geolocation', icon: MapPin, color: '#06B6D4',
    title: 'Geolocation Mapping',
    desc: 'Validate GPS coordinates with interactive map visualization. Detect clustering and GPS spoofing.',
    screenshotDark: '/geolocation_dark.png', screenshotLight: '/geolocation_light.png' },
  { id: 'textanalysis', label: 'Text Analysis', icon: FileSearch, color: '#64748B',
    title: 'Text Response Analysis',
    desc: 'Analyze open-ended responses for quality, recurring themes, and suspicious similar pairs.',
    screenshotDark: '/textanalysis_dark.png', screenshotLight: '/textanalysis_light.png' },
  { id: 'ai', label: 'AI Assessment', icon: Brain, color: '#EC4899',
    title: 'AI Quality Assessment',
    desc: 'ML-powered comprehensive quality scoring with Isolation Forest anomaly detection and straight-lining analysis.',
    screenshotDark: '/aidashboard_dark.png', screenshotLight: '/aidashboard_light.png' },
  { id: 'backchecks', label: 'Back-checks', icon: Clipboard, color: '#14B8A6',
    title: 'Back-check Analysis',
    desc: 'Compare primary and back-check surveys with IPA-inspired weighted error scoring and enumerator stats.',
    screenshotDark: '/backchecks_dark.png', screenshotLight: '/backchecks_light.png' },
];
 
const stats = [
  { end: 9,    suffix: '',  label: 'Quality Dimensions', decimals: 0 },
  { end: 85,   suffix: '%', label: 'Time Saved',         decimals: 0 },
  { end: 99.8, suffix: '%', label: 'Detection Accuracy', decimals: 1 },
];
 
// Signature Statement — words laid out across 3 lines, with semantic tokens
// that the scroll-driven "lit" progression and special effects reference.
const STATEMENT_WORDS = [
  { text: 'Survey',    line: 0 },
  { text: 'data',      line: 0 },
  { text: 'quality,',  line: 0 },
  { text: 'proven',    line: 1, accent: true },
  { text: ',',         line: 1, glue: true },
  { text: 'not',       line: 2 },
  { text: 'promised.', line: 2, strike: true },
];
 
// ============================================================================
// SHARED TOKENS + PLANS — exported for PricingPage.js
// ============================================================================
 
// Theme-aware color palette — resolved to inline styles for theme-awareness,
// while CSS variables in DataQuadLanding.css own the gradient + accents.
export const buildPalette = (isDarkMode) => ({
  bg:         isDarkMode ? '#08090B' : '#FFFFFF',
  bgAlt:      isDarkMode ? '#0E1015' : '#F7F8FA',
  bgCard:     isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  bgChip:     isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  border:     isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
  borderStrong: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
  text:       isDarkMode ? '#e9eaee' : '#111214',
  textSecondary: isDarkMode ? '#9a9ba3' : '#6B7280',
  textMuted:  isDarkMode ? '#5A5C66' : '#9CA3AF',
  accent:     '#2563EB',
  accentGold: '#ffb62f',
  accentGradient: 'linear-gradient(135deg, #105fc0 0%, #105fc0 15%, #2B8AFF 35%, #ffc55b 100%)',
  ctaFg:      '#FFFFFF',
  screenBg:   isDarkMode ? 'rgba(0,0,0,0.35)' : '#FFFFFF',
  chromeBg:   isDarkMode ? 'rgba(255,255,255,0.03)' : '#F4F5F7',
  chromeUrl:  isDarkMode ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
  dot:        isDarkMode ? 'rgba(255,255,255,0.12)' : '#DDDDDD',
});
 
export const getPlans = (isAnnualBilling) => ([
  { key: 'standard', name: 'Standard', icon: Database, color: '#3B82F6',
    price: isAnnualBilling ? 10 : 15, projects: '1',
    features: ['Full quality analysis', '9 dimension checks', 'Interactive dashboards', 'CSV & Excel exports'] },
  { key: 'pro', name: 'Pro', icon: Zap, color: '#8B5CF6',
    price: isAnnualBilling ? 15 : 20, projects: '3', popular: true,
    features: ['Everything in Standard', 'AI anomaly detection', 'Back-check validation', 'Priority support'] },
  { key: 'max', name: 'Max', icon: InfinityIcon, color: '#10B981',
    price: isAnnualBilling ? 80 : 100, projects: '∞',
    features: ['Everything in Pro', 'Unlimited team members', 'API access', 'Dedicated account manager'] },
]);
 
// ============================================================================
// LANDING PAGE
// ============================================================================
const DataQuadLanding = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [heroOn, setHeroOn] = useState(false);
 
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
 
  // Trigger hero word-reveal a beat after mount
  useEffect(() => {
    const t1 = setTimeout(() => setHeroOn(true), 60);
    const t2 = setTimeout(() => setHeroOn(true), 500);  // safety retry
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
 
  const scrollToSection = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);
 
  const c = buildPalette(isDarkMode);
 
  return (
    <div className="dq-landing" data-theme={isDarkMode ? 'dark' : 'light'} style={{ background: c.bg, color: c.text }}>
 
      {/* ================================================================
          NAVBAR
          ================================================================ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 24px',
        background: scrolled
          ? (isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)')
          : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.border}` : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', textDecoration: 'none', color: c.text }}
               onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={isDarkMode ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: '26px', height: '26px' }} />
            <span style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.3px' }}>dataQuad</span>
          </div>
 
          <div className="dq-nav-links" style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
            <button onClick={() => scrollToSection('features')} className="dq-nav-link"
              style={{ background: 'none', border: 'none', color: c.textSecondary, fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Features</button>
            <a href="/pricing" className="dq-nav-link"
               style={{ color: c.textSecondary, fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}>Pricing</a>
            <a href="/download" className="dq-nav-link"
               style={{ color: c.textSecondary, fontSize: '14px', fontWeight: '500', textDecoration: 'none' }}>Download</a>
          </div>
 
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={toggleTheme} aria-label="Toggle theme" style={{
              background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer',
              padding: '6px', display: 'flex', borderRadius: '8px', transition: 'background .2s, color .2s',
            }}>
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate('/login')} style={{
              background: 'none', border: 'none', color: c.textSecondary, fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'inherit', padding: '8px 12px', whiteSpace: 'nowrap',
            }}>Sign in</button>
            <button onClick={() => navigate('/login?signup=1')} className="dq-cta-btn" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: c.accent, color: '#FFFFFF', border: 'none',
              padding: '9px 20px', borderRadius: '10px', fontSize: '14px',
              fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}>Get Started</button>
          </div>
        </div>
      </nav>
 
      {/* ================================================================
          HERO
          ================================================================ */}
      <HeroSection
        isDarkMode={isDarkMode}
        c={c}
        heroOn={heroOn}
        navigate={navigate}
        scrollToSection={scrollToSection}
      />
 
      {/* ================================================================
          SIGNATURE STATEMENT (pinned word-reveal)
          ================================================================ */}
      {/*<StatementSection c={c} />*/}
 
      {/* ================================================================
          FEATURE SHOWCASE (pinned scroll)
          ================================================================ */}
      <FeatureShowcase features={features} isDarkMode={isDarkMode} c={c} />
 
      {/* ================================================================
          STATS
          ================================================================ */}
      <StatsSection stats={stats} isDarkMode={isDarkMode} c={c} />
 
      {/* ================================================================
          PLATFORMS
          ================================================================ */}
      <PlatformsSection isDarkMode={isDarkMode} c={c} navigate={navigate} />
 
      {/* ================================================================
          PRICING
          ================================================================ */}
      <PricingTeaser isDarkMode={isDarkMode} c={c} navigate={navigate} />
 
      {/* ================================================================
          CTA
          ================================================================ */}
      <CTASection isDarkMode={isDarkMode} c={c} navigate={navigate} />
 
      {/* ================================================================
          FOOTER
          ================================================================ */}
      <Footer isDarkMode={isDarkMode} c={c} logoSource={logoSource} logoSourceInverted={logoSourceInverted} />
 
      <style>{`
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}; }
      `}</style>
    </div>
  );
};
 
 
// ============================================================================
// HERO
// ============================================================================
const HeroSection = ({ isDarkMode, c, heroOn, navigate, scrollToSection }) => {
  const parallaxY = useParallaxY(0.25);
 
  // Word-delay indices match the original CD source for consistent choreography
  const heroWords = [
    { text: 'AI-Powered', d: 0, line: 0 },
    { text: 'Survey',     d: 1, line: 0 },
    { text: 'Data',       d: 2, line: 1 },
    { text: 'Quality',    d: 3, line: 1, pill: true },
    { text: 'Assessment', d: 4, line: 1 },
  ];
 
  const renderLine = (lineIdx) => (
    <span className="dq-word-line" key={lineIdx}>
      {heroWords.filter(w => w.line === lineIdx).map((w, i, arr) => (
        <React.Fragment key={w.text}>
          <span className="dq-word-mask">
            <span
              className={`dq-word ${w.pill ? 'dq-hero-pillword' : ''}`}
              style={{ '--dq-d': w.d }}
            >
              {w.text}
            </span>
          </span>
          {i < arr.length - 1 && ' '}
        </React.Fragment>
      ))}
    </span>
  );
 
  return (
    <section id="top" style={{
      position: 'relative',
      padding: '140px 24px 100px',
      textAlign: 'center',
      overflow: 'hidden',
      minHeight: '90vh',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      {/* Parallax glow */}
      <div aria-hidden style={{
        position: 'absolute', width: '820px', height: '820px', borderRadius: '50%',
        top: '-220px', left: '50%',
        transform: `translate(-50%, ${parallaxY}px)`,
        background: isDarkMode
          ? 'radial-gradient(circle, rgba(43,138,255,0.14) 0%, transparent 70%)'
          : 'radial-gradient(circle, rgba(43,138,255,0.07) 0%, transparent 70%)',
        pointerEvents: 'none',
        willChange: 'transform',
      }} />
 
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <img
            className="dq-hero-logo"
            src={isDarkMode ? logoSourceInverted : logoSource}
            alt=""
            style={{ width: '64px', height: '64px' }}
          />
        </div>
 
        <h1 className={`dq-hero-h1 ${heroOn ? 'on' : ''}`} style={{
          fontSize: 'clamp(38px, 5.4vw, 72px)',
          fontWeight: '700', letterSpacing: '-2.2px', lineHeight: 1.06,
          margin: '0 auto 32px', maxWidth: '900px', color: c.text,
        }}>
          {renderLine(0)}
          {renderLine(1)}
        </h1>
 
        <p className={`dq-reveal ${heroOn ? 'on' : ''}`} style={{
          fontSize: 'clamp(16px, 1.6vw, 20px)', lineHeight: 1.65,
          color: c.textSecondary, maxWidth: '620px', margin: '0 auto 36px',
          transitionDelay: '600ms',
        }}>
          Connect KoBoToolbox, SurveyCTO, or ODK and get comprehensive quality analysis across 9 dimensions — in minutes, not days.
        </p>
 
        <div className={`dq-reveal ${heroOn ? 'on' : ''}`} style={{
          display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
          marginBottom: '14px', transitionDelay: '750ms',
        }}>
          <button onClick={() => navigate('/login?signup=1')} className="dq-cta-btn" style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: c.accent, color: '#FFFFFF', border: 'none',
            padding: '14px 32px', borderRadius: '12px', fontSize: '16px',
            fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Start Free Trial <ArrowRight size={18} />
          </button>
          <button onClick={() => scrollToSection('statement')} style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: c.bgChip, color: c.text, border: `1px solid ${c.border}`,
            padding: '14px 32px', borderRadius: '12px', fontSize: '16px',
            fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
            transition: 'border-color .2s',
          }}>
            See How It Works
          </button>
        </div>
 
        <p className={`dq-reveal dq-mono ${heroOn ? 'on' : ''}`} style={{
          fontSize: '13px', color: c.textMuted, transitionDelay: '900ms', margin: '0 0 28px',
        }}>
          15-day free trial · no credit card required
        </p>
 
        <p className={`dq-reveal ${heroOn ? 'on' : ''}`} style={{
          fontSize: '13px', fontWeight: '500', color: c.textMuted,
          transitionDelay: '1050ms', margin: 0,
        }}>
          Trusted by research teams worldwide
        </p>
      </div>
 
      {/* Scroll hint */}
      <div aria-hidden style={{
        position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
      }}>
        <span className="dq-mono" style={{
          fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
          color: c.textMuted,
        }}>
          Scroll
        </span>
        <span className="dq-scroll-line" style={{
          width: '1px', height: '44px', background: c.borderStrong, display: 'block',
        }} />
      </div>
    </section>
  );
};
 
 
// ============================================================================
// SIGNATURE STATEMENT — static typographic block, all words pre-lit
// ============================================================================
/*{
const StatementSection = ({ c }) => {
  // All words lit by default — matches CD's final-state look without
  // scroll-driven progression. The accent gradient on "proven" and the gold
  // strike on "promised." come from CSS on `.lit.accent` / `.lit.strike`.
  const litCount = STATEMENT_WORDS.length;
 
  const renderLine = (lineIdx) => {
    const words = STATEMENT_WORDS.map((w, gi) => ({ ...w, gi })).filter(w => w.line === lineIdx);
    return (
      <span className="dq-s-line" key={lineIdx}>
        {words.map((w, i) => {
          const classes = [
            'dq-s-word',
            w.accent ? 'accent' : '',
            w.strike ? 'strike' : '',
            w.glue ? 'glue' : '',
            w.gi < litCount ? 'lit' : '',
          ].filter(Boolean).join(' ');
          if (w.glue) {
            // Comma attached to previous word — no surrounding whitespace
            return <span key={i} className={classes}>{w.text}</span>;
          }
          return (
            <React.Fragment key={i}>
              <span className={classes}>{w.text}</span>
              {i < words.length - 1 && !words[i + 1]?.glue && ' '}
            </React.Fragment>
          );
        })}
      </span>
    );
  };
 
  return (
    <section
      id="statement"
      className="dq-statement-wrap"
      style={{
        background: c.bgAlt,
        borderTop: `1px solid ${c.border}`,
        borderBottom: `1px solid ${c.border}`,
      }}
    >
      <div className="dq-statement-inner">
        <div style={{ alignSelf: 'flex-start', marginBottom: '28px' }}>
          <span className="dq-kicker">The DataQuad promise</span>
        </div>
        <h2 className="dq-statement-h2" style={{ alignSelf: 'flex-start' }}>
          {renderLine(0)}
          {renderLine(1)}
          {renderLine(2)}
        </h2>
        <p style={{
          fontSize: 'clamp(16px, 1.3vw, 19px)',
          color: c.textSecondary, lineHeight: 1.6,
          maxWidth: '560px', alignSelf: 'flex-start',
          margin: '0 0 28px',
        }}>
          Every claim we make is testable. Every dimension has a number. Every flagged record links back to its evidence.
        </p>
        <div style={{
          display: 'flex', gap: '28px', flexWrap: 'wrap', alignSelf: 'flex-start',
          fontFamily: 'var(--dq-font-mono)', fontSize: '12px',
          color: c.textMuted, letterSpacing: '0.5px',
        }}>
          {['9 quality dimensions', 'Empirically-inspired scoring', 'Auditable by design'].map((marker) => (
            <span key={marker} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <i style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: c.accent, display: 'inline-block',
              }} />
              {marker}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
}*/
 
// ============================================================================
// FEATURE SHOWCASE — static stage with 5s auto-rotate through features.
// Click-to-select is supported; the auto-rotate timer resets on each change.
// On mobile (≤768px), the screenshot panel renders inline right below the
// active list item (not below the last item). On desktop it lives in the
// right grid column as before.
// ============================================================================
const FeatureShowcase = ({ features, isDarkMode, c }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
 
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
 
  // Auto-rotate every 5s. Using activeIndex in deps means every change
  // (manual click or auto-tick) clears and re-sets the timer cleanly.
  useEffect(() => {
    const t = setTimeout(() => {
      setActiveIndex(prev => (prev + 1) % features.length);
    }, 5000);
    return () => clearTimeout(t);
  }, [activeIndex, features.length]);
 
  const handleItemClick = useCallback((i) => {
    setActiveIndex(i);
  }, []);
 
  const progressPct = ((activeIndex + 1) / features.length) * 100;
 
  // Screenshot panel — rendered once, placed either inline in the list (mobile)
  // or in the right grid column (desktop). Single source of truth for the markup.
  const screenPanel = (
    <div className="dq-stage-screen" style={{
      background: c.screenBg, border: `1px solid ${c.border}`,
      boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.35)' : '0 16px 48px rgba(0,0,0,0.08)',
    }}>
      <div className="dq-browser-chrome" style={{
        background: c.chromeBg,
        borderBottom: `1px solid ${c.border}`,
      }}>
        <div className="dq-chrome-dots">
          <i style={{ background: c.dot }} />
          <i style={{ background: c.dot }} />
          <i style={{ background: c.dot }} />
        </div>
        <div className="dq-chrome-url" style={{
          background: c.chromeUrl, color: c.textMuted,
        }}>
          <Globe size={11} />
          dataquadapp.com/app
        </div>
      </div>
      <div className="dq-screen-stack" style={{ background: c.bgAlt }}>
        {features.map((f, i) => (
          <img
            key={f.id}
            src={isDarkMode ? f.screenshotDark : f.screenshotLight}
            alt={f.title}
            className={i === activeIndex ? 'active' : ''}
          />
        ))}
      </div>
    </div>
  );
 
  return (
    <section id="features" style={{
      position: 'relative',
      padding: '60px 0 80px',
    }}>
      {/* Intro — tighter bottom margin */}
      <div style={{
        maxWidth: '900px', margin: '0 auto 32px',
        textAlign: 'center', padding: '0 24px',
      }}>
        <span className="dq-kicker">9 quality dimensions</span>
        <h2 style={{
          fontSize: 'clamp(28px, 3.5vw, 46px)', fontWeight: '700',
          letterSpacing: '-1.2px', lineHeight: 1.1,
          margin: '12px 0 14px', color: c.text, textWrap: 'balance',
        }}>
          Everything you need to catch bad data.
        </h2>
        <p style={{
          fontSize: '16px', lineHeight: 1.65, color: c.textSecondary,
          maxWidth: '560px', margin: '0 auto',
        }}>
          Each dimension runs automatically when you upload or connect your survey data — the dashboard updates live.
        </p>
      </div>
 
      {/* Stage — static, no pinned scroll */}
      <div className="dq-features-stage">
        <div className="dq-stage-inner">
          {/* LEFT (desktop) / ONLY COLUMN (mobile): stage list */}
          <div className="dq-stage-list" style={{ color: c.textMuted }}>
            {features.map((f, i) => {
              const isActive = activeIndex === i;
              return (
                <React.Fragment key={f.id}>
                  <button
                    className={`dq-stage-item ${isActive ? 'active' : ''}`}
                    onClick={() => handleItemClick(i)}
                    style={{
                      color: isActive ? c.text : c.textMuted,
                      background: isActive ? c.bgChip : 'transparent',
                    }}
                  >
                    <span className="dq-si-row">
                      <span className="dq-si-dot" style={{
                        background: isActive ? c.accent : c.borderStrong,
                      }} />
                      <span className="dq-si-idx" style={{ color: c.textMuted }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="dq-si-name">{f.label}</span>
                    </span>
                    <span className="dq-si-desc" style={{ color: c.textSecondary }}>
                      {f.desc}
                    </span>
                  </button>
                  {/* Mobile-only: screenshot appears right under the active item */}
                  {isMobile && isActive && screenPanel}
                </React.Fragment>
              );
            })}
          </div>
 
          {/* RIGHT (desktop only): screenshot in the grid's second column */}
          {!isMobile && screenPanel}
        </div>
 
        {/* Progress */}
        <div className="dq-stage-progress" style={{ color: c.textMuted }}>
          <div className="dq-progress-track" style={{ background: c.border }}>
            <div className="dq-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div style={{ color: c.textMuted }}>
            {String(activeIndex + 1).padStart(2, '0')} / {String(features.length).padStart(2, '0')}
          </div>
        </div>
      </div>
    </section>
  );
};
 
 
// ============================================================================
// STATS
// ============================================================================
const AnimatedStat = ({ end, suffix, label, decimals, isVisible, delay, c }) => {
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (isVisible) {
      const t = setTimeout(() => setStarted(true), delay);
      return () => clearTimeout(t);
    }
  }, [isVisible, delay]);
  const value = useCountUp(end, 1600, started);
  const display = decimals > 0 ? value.toFixed(decimals) : Math.round(value);
  return (
    <div className={`dq-stat ${started ? 'on' : ''}`} style={{ textAlign: 'center' }}>
      <div className="dq-stat-num" style={{
        fontSize: 'clamp(40px, 5.5vw, 72px)',
        fontWeight: '700', letterSpacing: '-2px', marginBottom: '8px', lineHeight: 1,
      }}>
        {display}{suffix}
      </div>
      <div className="dq-stat-underline" />
      <div className="dq-mono" style={{
        fontSize: '13px', color: c.textMuted, textTransform: 'uppercase', letterSpacing: '1px',
      }}>
        {label}
      </div>
    </div>
  );
};
 
const StatsSection = ({ stats, isDarkMode, c }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section ref={ref} style={{ padding: '100px 24px' }}>
      <div style={{
        maxWidth: '900px', margin: '0 auto',
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px',
      }}>
        {stats.map((s, i) => (
          <AnimatedStat key={i} end={s.end} suffix={s.suffix} label={s.label} decimals={s.decimals}
                        isVisible={isVisible} delay={i * 200} c={c} />
        ))}
      </div>
    </section>
  );
};
 
 
// ============================================================================
// PLATFORMS
// ============================================================================
const PlatformsSection = ({ isDarkMode, c, navigate }) => {
  const [ref, isVisible] = useScrollReveal();
  const categories = [
    { label: 'File Upload',    items: ['Excel (.xlsx, .xls)', 'CSV (.csv)', 'Stata (.dta)', 'SQLite (.db)'] },
    { label: 'API Connection', items: ['KoBoToolbox', 'SurveyCTO', 'ODK Central', 'Survey Solutions'] },
    { label: 'Database',       items: ['PostgreSQL', 'MySQL', 'MariaDB', 'MongoDB', 'DynamoDB'] },
  ];
 
  return (
    <section id="download" ref={ref} style={{
      padding: '100px 24px',
      background: c.bgAlt,
      borderTop: `1px solid ${c.border}`,
      borderBottom: `1px solid ${c.border}`,
    }}>
      <div style={{
        maxWidth: '1000px', margin: '0 auto', textAlign: 'center',
        opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: 'all 0.7s ease',
      }}>
        <span className="dq-kicker">Integrations</span>
        <h2 style={{
          fontSize: 'clamp(28px, 3.5vw, 46px)', fontWeight: '700',
          letterSpacing: '-1.2px', lineHeight: 1.1,
          margin: '12px 0 44px', color: c.text, textWrap: 'balance',
        }}>
          Works with your existing tools.
        </h2>
 
        <div style={{
          display: 'grid', gap: '20px',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          textAlign: 'left',
        }}>
          {categories.map((cat, ci) => (
            <div key={ci} className="dq-platform-card" style={{
              padding: '28px 24px', borderRadius: '14px',
              background: c.bg, border: `1px solid ${c.border}`,
            }}>
              <div className="dq-mono" style={{
                fontSize: '11px', fontWeight: '700',
                letterSpacing: '1.5px', textTransform: 'uppercase',
                color: c.accent, marginBottom: '18px',
              }}>
                {cat.label}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0,
                           display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {cat.items.map((item, ii) => (
                  <li key={ii} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    fontSize: '14px', color: c.textSecondary,
                  }}>
                    <span style={{
                      width: '5px', height: '5px', borderRadius: '50%',
                      background: c.accent, display: 'inline-block', flexShrink: 0,
                    }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
 
        <div style={{ marginTop: '36px', display: 'flex', gap: '14px',
                      justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { icon: Monitor,    label: 'Web App' },
            { icon: Download,   label: 'Windows' },
            { icon: Smartphone, label: 'Android' },
          ].map((p, i) => (
            <span key={i} onClick={() => navigate('/download')}
                  className="dq-chip" style={{
              background: c.bg, border: `1px solid ${c.border}`,
              color: c.textSecondary, cursor: 'pointer',
              transition: 'border-color .2s',
            }}>
              <p.icon size={14} style={{ color: c.accent }} />
              {p.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
 
 
// ============================================================================
// PRICING TEASER — full pricing lives on its own page at /pricing
// ============================================================================
const PricingTeaser = ({ isDarkMode, c, navigate }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section id="pricing" ref={ref} style={{ padding: '120px 24px', textAlign: 'center' }}>
      <div style={{
        maxWidth: '760px', margin: '0 auto',
        opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'all 0.7s ease',
      }}>
        <span className="dq-kicker">Pricing</span>
        <h2 style={{
          fontSize: 'clamp(28px, 3.5vw, 46px)', fontWeight: '700',
          letterSpacing: '-1.2px', lineHeight: 1.1,
          margin: '12px 0 14px', color: c.text, textWrap: 'balance',
        }}>
          Simple, transparent pricing.
        </h2>
        <p style={{ fontSize: '16px', color: c.textSecondary, margin: '0 auto 28px', maxWidth: '520px', lineHeight: 1.65 }}>
          Plans from $10/month. Start with a 15-day free trial and upgrade when you're ready.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '18px' }}>
          <a href="/pricing" className="dq-cta-btn" style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: c.accent, color: '#FFFFFF', textDecoration: 'none',
            padding: '13px 28px', borderRadius: '12px', fontSize: '15px',
            fontWeight: '600', fontFamily: 'inherit',
          }}>
            See pricing <ArrowRight size={16} />
          </a>
          <button onClick={() => navigate('/login?signup=1')} style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: c.bgChip, color: c.text, border: `1px solid ${c.border}`,
            padding: '13px 28px', borderRadius: '12px', fontSize: '15px',
            fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
            transition: 'border-color .2s',
          }}>
            Start Free Trial
          </button>
        </div>
        <p className="dq-mono" style={{ fontSize: '13px', color: c.textMuted, margin: 0 }}>
          All plans include a 14-day refund policy · Payments secured by Paddle
        </p>
      </div>
    </section>
  );
};
 
 
// ============================================================================
// CTA
// ============================================================================
const CTASection = ({ isDarkMode, c, navigate }) => {
  const [ref, isVisible] = useScrollReveal(0.35);
 
  const ctaWords = [
    ['Ready', 'to', 'improve'],
    ['your', 'data quality', '?'],
  ];
 
  return (
    <section ref={ref} style={{
      padding: '120px 24px',
      background: c.bgAlt,
      borderTop: `1px solid ${c.border}`,
      borderBottom: `1px solid ${c.border}`,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <h2 className={`dq-cta-h2 ${isVisible ? 'on' : ''}`} style={{ color: c.text }}>
          {ctaWords.map((line, li) => (
            <span className="dq-cta-line" key={li}>
              {line.map((w, wi) => {
                const isGradient = w === 'data quality';
                const isPunct = w === '?';
                return (
                  <React.Fragment key={wi}>
                    <span className={`dq-cta-word ${isGradient ? 'dq-gradient-word' : ''}`}
                          style={isPunct ? { padding: 0 } : undefined}>
                      {w}
                    </span>
                    {wi < line.length - 1 && !isPunct && line[wi + 1] !== '?' && ' '}
                  </React.Fragment>
                );
              })}
            </span>
          ))}
        </h2>
        <p className={`dq-reveal ${isVisible ? 'on' : ''}`} style={{
          fontSize: '16px', color: c.textSecondary, lineHeight: 1.65,
          margin: '0 auto 28px', maxWidth: '560px',
        }}>
          Join research teams who have moved from manual spot-checks to automated, comprehensive quality assurance.
        </p>
        <button
          onClick={() => navigate('/login?signup=1')}
          className={`dq-cta-btn dq-reveal ${isVisible ? 'on' : ''}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: c.accent, color: '#FFFFFF', border: 'none',
            padding: '16px 36px', borderRadius: '12px', fontSize: '16px',
            fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
            transitionDelay: '150ms',
          }}>
          Get Started Free <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
};
 
 
// ============================================================================
// FOOTER
// ============================================================================
export const Footer = ({ isDarkMode, c, logoSource, logoSourceInverted }) => {
  return (
    <footer style={{ padding: '72px 24px 32px' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr repeat(3, 1fr)',
          gap: '48px', marginBottom: '36px',
        }}>
          {/* Brand */}
          <div style={{ maxWidth: '260px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                          marginBottom: '12px', fontWeight: '600', fontSize: '16px' }}>
              <img src={isDarkMode ? logoSourceInverted : logoSource} alt=""
                   style={{ width: '22px', height: '22px' }} />
              <span style={{ color: c.text }}>dataQuad</span>
            </div>
            <p style={{ fontSize: '13px', lineHeight: 1.6, color: c.textMuted, margin: 0 }}>
              AI-powered survey data quality assessment by Noble Syntax Limited.
            </p>
            {/* Social icons — preserved from existing landing */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              {[
                { label: 'LinkedIn',  href: 'https://www.linkedin.com/company/dataquadapp',
                  svg: <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/> },
                { label: 'Instagram', href: 'https://www.instagram.com/dataquadapp',
                  svg: <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/> },
                { label: 'YouTube',   href: 'https://www.youtube.com/@noblesyntax',
                  svg: <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/> },
              ].map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '30px', height: '30px', borderRadius: '7px',
                  background: c.bgChip, border: `1px solid ${c.border}`,
                  color: c.textMuted, textDecoration: 'none',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">{s.svg}</svg>
                </a>
              ))}
            </div>
          </div>
 
          {/* Product / Legal / Contact */}
          {[
            { title: 'Product', links: [
              { label: 'Pricing',       href: '/pricing' },
              { label: 'Download',      href: '/download' },
              { label: 'Noble Syntax',  href: 'https://noblesyntax.com' },
            ]},
            { title: 'Legal', links: [
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Privacy Policy',   href: '/privacy' },
              { label: 'Refund Policy',    href: '/refund' },
            ]},
            { title: 'Contact', links: [
              { label: 'support@noblesyntax.com', href: 'mailto:support@noblesyntax.com' },
              { label: 'Lusaka, Zambia' },
            ]},
          ].map((group, gi) => (
            <div key={gi} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div className="dq-mono" style={{
                fontSize: '11px', fontWeight: '700',
                letterSpacing: '1.5px', textTransform: 'uppercase',
                color: c.textMuted, marginBottom: '4px',
              }}>
                {group.title}
              </div>
              {group.links.map((link, li) => (
                link.href
                  ? <a key={li} href={link.href} style={{
                      fontSize: '13px', color: c.textSecondary, textDecoration: 'none',
                    }}>{link.label}</a>
                  : <span key={li} style={{
                      fontSize: '13px', color: c.textSecondary,
                    }}>{link.label}</span>
              ))}
            </div>
          ))}
        </div>
 
        <div className="dq-mono" style={{
          paddingTop: '20px', borderTop: `1px solid ${c.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: '8px',
          fontSize: '12px', color: c.textMuted,
        }}>
          <span>© {new Date().getFullYear()} Noble Syntax Limited. All rights reserved.</span>
          <span>Payments secured by Paddle</span>
        </div>
      </div>
    </footer>
  );
};
 
 
export default DataQuadLanding;